import { useCallback, useState } from 'react';

import type { CopiedValue, CopyFn } from '.';

/**
 * React hook to copy text to the clipboard.
 * @returns [lastCopied, copyFn] - Last copied value and async copy function.
 * @example
 * const [copied, copy] = useCopyToClipboard();
 * <button onClick={() => copy('Hi')}>Copy</button>
 */
export function useCopyToClipboard(): [CopiedValue, CopyFn] {
  const [copiedText, setCopiedText] = useState<CopiedValue>(null);

  /**
   * Attempts to write the provided text to the clipboard.
   * Updates the state if successful, otherwise logs a warning and resets the state.
   *
   * @param {string} text - The text to copy to the clipboard.
   * @returns {Promise<boolean>} Resolves to `true` if the copy succeeded, `false` otherwise.
   */
  const copy: CopyFn = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      return true;
    } catch (error) {
      console.warn('Copy failed', error);
      setCopiedText(null);
      return false;
    }
  }, []);

  return [copiedText, copy];
}
